import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Users, BookOpen, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ModuleEnrollmentReport({ user }) {
  const { moduleId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadReport();
  }, [moduleId]);

  const loadReport = async () => {
    try {
      const response = await fetch(`${API}/modules/${moduleId}/report`);
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        toast.error('Failed to load report');
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading report...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Report not found</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            {report.module.name}
          </h1>
          <p className="text-lg text-[#588157]">{report.module.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl" data-testid="stat-total-courses">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#3A5A40]/10 rounded-xl">
                <BookOpen className="w-6 h-6 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Total Courses</p>
                <p className="text-3xl font-bold text-[#344E41]">{report.total_courses}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl" data-testid="stat-total-students">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4A373]/10 rounded-xl">
                <Users className="w-6 h-6 text-[#D4A373]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Enrolled Students</p>
                <p className="text-3xl font-bold text-[#344E41]">{report.total_students}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl" data-testid="stat-avg-completion">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#588157]/10 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-[#588157]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Avg Completion</p>
                <p className="text-3xl font-bold text-[#344E41]">
                  {report.students.length > 0
                    ? Math.round(report.students.reduce((acc, s) => acc + s.completion_rate, 0) / report.students.length)
                    : 0}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        <h2 className="text-2xl font-bold text-[#344E41] mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
          Student Progress
        </h2>

        <div className="space-y-4">
          {report.students.map((student) => (
            <Card
              key={student.user_id}
              className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 rounded-2xl"
              data-testid={`student-${student.user_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#344E41]">{student.name}</h3>
                  <p className="text-sm text-[#588157]">{student.email}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#3A5A40]">{student.completion_rate}%</div>
                  <p className="text-xs text-[#588157]">Completion Rate</p>
                </div>
              </div>

              <div className="mb-4">
                <Progress value={student.completion_rate} className="h-2" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-[#3A5A40]" strokeWidth={1.5} />
                    <span className="font-bold text-[#344E41]">Completed ({student.completed_courses.length})</span>
                  </div>
                  {student.completed_courses.length === 0 ? (
                    <p className="text-sm text-[#A3B18A] ml-7">No courses completed yet</p>
                  ) : (
                    <ul className="space-y-1 ml-7">
                      {student.completed_courses.map((course) => (
                        <li key={course.course_id} className="text-sm text-[#344E41]">
                          • {course.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-[#D4A373]" strokeWidth={1.5} />
                    <span className="font-bold text-[#344E41]">Pending ({student.pending_courses.length})</span>
                  </div>
                  {student.pending_courses.length === 0 ? (
                    <p className="text-sm text-[#A3B18A] ml-7">All courses completed!</p>
                  ) : (
                    <ul className="space-y-1 ml-7">
                      {student.pending_courses.map((course) => (
                        <li key={course.course_id} className="text-sm text-[#344E41]">
                          • {course.title} <span className="text-[#588157]">({course.progress}%)</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          ))}

          {report.students.length === 0 && (
            <Card className="p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
              <Users className="w-16 h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#588157]">No students enrolled in this module yet</p>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
